import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  Target,
  Activity,
  CheckCircle,
  ArrowRight,
  Gauge,
  PieChart,
  LineChart,
  AlertCircle,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function PerformanceTrackingPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const trackingFeatures = [
    {
      icon: Target,
      title: 'Objective Measures',
      description:
        'Choose measures for your strategic objectives so progress reviews stay focused on what matters.',
      capabilities: [
        'Objective measures',
        'Measures that fit the objective',
        'Clear review questions',
        'Strategic context',
      ],
    },
    {
      icon: BarChart3,
      title: 'Progress Visualization',
      description: 'Review the progress of projects and tasks alongside the strategy they support.',
      capabilities: [
        'Project progress',
        'Task status',
        'Team ownership',
        'Connected strategic priorities',
      ],
    },
    {
      icon: Activity,
      title: 'Task Monitoring',
      description:
        'Monitor all strategic tasks, assignments, and deliverables in one centralized view.',
      capabilities: [
        'Task status tracking',
        'Task ownership',
        'Tasks and subtasks',
        'Progress updates',
      ],
    },
    {
      icon: TrendingUp,
      title: 'Progress Reviews',
      description:
        'Use a regular review rhythm to discuss progress, changing assumptions and the next practical action.',
      capabilities: ['Review questions', 'Review priorities', 'Assessment context', 'Next actions'],
    },
    {
      icon: AlertCircle,
      title: 'Clear Ownership',
      description:
        'See who owns the work and use progress reviews to decide where support is needed.',
      capabilities: [
        'Assigned owners',
        'People and roles',
        'Project responsibility',
        'Team context',
      ],
    },
    {
      icon: PieChart,
      title: 'Custom Reports',
      description:
        'Generate comprehensive reports for board meetings, reviews, and stakeholder updates.',
      capabilities: [
        'Executive summaries',
        'PDF exports',
        'Custom templates',
        'Visual report layouts',
      ],
    },
  ];

  const metrics = [
    {
      category: 'Strategic Objectives',
      examples: [
        'Revenue growth %',
        'Market share increase',
        'Customer acquisition cost',
        'Net promoter score',
      ],
    },
    {
      category: 'Project Execution',
      examples: [
        'Projects on track %',
        'Budget variance',
        'Resource utilization',
        'Milestone completion rate',
      ],
    },
    {
      category: 'Team Performance',
      examples: [
        'Task completion rate',
        'Average time to complete',
        'Team capacity',
        'Cross-functional collaboration',
      ],
    },
    {
      category: 'Business Impact',
      examples: ['ROI per initiative', 'Cost savings', 'Efficiency gains', 'Customer satisfaction'],
    },
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: 'Review progress in context',
      description:
        'See how projects and tasks support the objective, then decide what needs attention.',
    },
    {
      icon: TrendingUp,
      title: 'Make the next decision clearer',
      description:
        'Use your measures and progress updates to discuss what to continue, change or stop.',
    },
    {
      icon: Target,
      title: 'Make ownership visible',
      description: 'Connect each priority to the people responsible for moving the work forward.',
    },
    {
      icon: Gauge,
      title: 'Surface questions sooner',
      description:
        'Use regular reviews to spot stalled work, unclear ownership and changing assumptions.',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb items={[{ label: 'Performance Tracking' }]} />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        title="See what is moving. Decide what needs attention."
        subtitle="With Pro Activation, connect objectives to measures, projects and tasks so you can review progress with context. Make the next decision from a clear view of the work."
      >
        <Link
          to="/sign-up"
          className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          Put my plan into motion
        </Link>
      </PageHero>

      {/* Tracking Features */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">
              A clearer view of the work behind your goals.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to monitor, measure, and optimize your strategic execution
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {trackingFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="bg-blue-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.capabilities.map((capability, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>{capability}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Can Track */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">
              Choose the measures that matter to your plan.
            </h2>
            <p className="text-xl text-gray-600">
              From high-level strategy to day-to-day execution
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {metrics.map((metric, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white p-6 rounded-xl shadow-sm"
              >
                <h3 className="text-2xl font-bold mb-4 text-blue-600">{metric.category}</h3>
                <ul className="space-y-3">
                  {metric.examples.map((example, idx) => (
                    <li key={idx} className="flex items-start">
                      <div className="bg-blue-100 w-2 h-2 rounded-full mr-3 mt-2 flex-shrink-0" />
                      <span className="text-gray-700">{example}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">From a clear objective to a useful review.</h2>
            <p className="text-xl text-gray-600">Simple process to monitor strategic execution</p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: '1',
                  icon: Target,
                  title: 'Define the Objective',
                  description:
                    'Choose a clear objective and the measure you will use to discuss progress',
                  example:
                    'Example: Build a repeatable referral channel and review the qualified leads it brings in',
                },
                {
                  step: '2',
                  icon: Activity,
                  title: 'Review Project Progress',
                  description:
                    'Keep your measures and project updates current so each review reflects the latest progress',
                  example:
                    'Example: The referral offer is defined, an owner is assigned and outreach is in progress',
                },
                {
                  step: '3',
                  icon: LineChart,
                  title: 'Discuss the Gaps',
                  description:
                    'Review project and task progress alongside your visual strategic report to see what needs attention',
                  example:
                    'Example: The referral project is moving, but the outreach task needs a decision before it can continue',
                },
                {
                  step: '4',
                  icon: ArrowRight,
                  title: 'Choose the Next Action',
                  description:
                    'Turn the review into a clear action, with an owner and a place in the plan',
                  example:
                    'Example: Assign the outreach task to your sales lead and agree on the next milestone',
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="public-card flex items-start space-x-6 bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-200"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-lg flex items-center justify-center font-bold text-xl">
                    {item.step}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <item.icon className="w-5 h-5 text-blue-600 mr-2" />
                      <h3 className="text-xl font-bold">{item.title}</h3>
                    </div>
                    <p className="text-gray-700 mb-2">{item.description}</p>
                    <p className="text-sm text-gray-600 italic">{item.example}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Make progress easier to discuss and act on.</h2>
            <p className="text-xl text-gray-600">Turn your strategy into measurable results</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card text-center bg-white p-6 rounded-xl shadow-sm"
              >
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Impact */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card text-gray-900 p-12 rounded-2xl text-center bg-blue-50"
            >
              <h2 className="text-4xl font-bold mb-6">
                Turn progress into a practical conversation.
              </h2>
              <div className="grid md:grid-cols-3 gap-8 mt-8">
                <div>
                  <div className="text-2xl font-bold mb-2">Direction</div>
                  <p className="text-gray-600">
                    Keep each measure connected to the objective it supports.
                  </p>
                </div>
                <div>
                  <div className="text-2xl font-bold mb-2">Ownership</div>
                  <p className="text-gray-600">
                    Know who can move the work forward and where support is needed.
                  </p>
                </div>
                <div>
                  <div className="text-2xl font-bold mb-2">Next steps</div>
                  <p className="text-gray-600">
                    Turn your review into a practical decision about what happens next.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Know where to focus next.</h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="text-xl font-bold mb-4 text-red-600">
                  Without Performance Tracking
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li>❌ "Are we on track?" → "I think so...?"</li>
                  <li>❌ Discover problems when it's too late</li>
                  <li>❌ Annual reviews reveal missed opportunities</li>
                  <li>❌ Gut feelings drive strategic decisions</li>
                  <li>❌ No accountability for results</li>
                  <li>❌ Can't justify resource investments</li>
                </ul>
              </div>

              <div className="public-card bg-blue-50 p-6 rounded-xl border border-blue-200">
                <h3 className="text-xl font-bold mb-4 text-blue-600">With StratPlan Tracking</h3>
                <ul className="space-y-3 text-gray-700">
                  <li>✅ Projects and tasks shown in their strategic context</li>
                  <li>✅ Regular reviews to identify where attention is needed</li>
                  <li>✅ Clear questions for each progress review</li>
                  <li>✅ Data-backed decisions with confidence</li>
                  <li>✅ Clear ownership and results</li>
                  <li>✅ A clearer basis for resource decisions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="public-section public-cta py-16 bg-blue-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={false} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Keep your strategy moving.</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Connect the goals you have set to the work your team is doing. Use each review to
              choose a clear next step.
            </p>
            <Link
              to="/sign-up"
              className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Build my strategic plan <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
}
